import React, { useMemo } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  useTheme,
  alpha,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Store as StoreIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  LocalPizza as PizzaIcon,
  Restaurant as RestaurantIcon,
  Fastfood as FastfoodIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';
import { useStores } from '../hooks/useStores';
import { useStoresWithSales } from '../hooks/useStoresWithSales';

interface BrandData {
  brand: string;
  stores: number;
  revenue: number;
  orders: number;
  avgOrderValue: number;
  customers: number;
  growth: number;
  topStore: string;
  performance: number;
}

interface BrandOverviewProps {
  selectedStores: string[];
  period?: 'today' | 'week' | 'month' | 'year';
  onBrandClick?: (brand: string) => void;
}

const brandIcons: Record<string, React.ReactElement> = {
  "Happy's Pizza": <PizzaIcon />,
  "Savvy Sliders": <FastfoodIcon />,
  "BurgerFi": <RestaurantIcon />,
  "Fat Boys Pizza": <PizzaIcon />,
};

const brandColors: Record<string, string> = {
  "Happy's Pizza": '#FF6B6B',
  "Savvy Sliders": '#4ECDC4',
  "BurgerFi": '#AF2326',
  "Fat Boys Pizza": '#FFD93D',
};

const BrandOverview: React.FC<BrandOverviewProps> = ({ selectedStores, period = 'today', onBrandClick }) => {
  const theme = useTheme();
  // Use the date that has data in the database (September 28, 2025)
  // Database stores dates in GMT/UTC, so Sep 27 at 18:30 becomes Sep 28
  const dataDate = '2025-09-28';

  // Get real data from APIs
  const { data: storesData, isLoading: isStoresLoading } = useStores();
  const { data: storesWithSales, isLoading: isSalesLoading } = useStoresWithSales(dataDate);

  // Function to detect brand from store name
  const detectBrand = (storeName: string): string => {
    const name = storeName.toLowerCase();
    if (name.includes('happy')) return "Happy's Pizza";
    if (name.includes('savvy')) return 'Savvy Sliders';
    if (name.includes('burger')) return 'BurgerFi';
    if (name.includes('fat boy')) return 'Fat Boys Pizza';
    return 'Other';
  };

  // Calculate brand data from real API data
  const brandData: BrandData[] = useMemo(() => {
    // Use stores with sales data if available, otherwise fall back to basic store data
    const storesToUse = storesWithSales || storesData;
    if (!storesToUse || !Array.isArray(storesToUse)) {
      return [];
    }

    // If selected stores, filter to only those
    const filteredStores = selectedStores.length > 0
      ? (storesToUse as any[]).filter((store: any) => selectedStores.includes(store.id))
      : storesToUse;

    const brandMetrics: Record<string, any> = {};
    
    // Calculate metrics per brand from actual store sales
    filteredStores.forEach((store: any) => {
      const brand = store.brand || detectBrand(store.name || store.storeName);
      
      if (!brandMetrics[brand]) {
        brandMetrics[brand] = {
          brand,
          stores: [],
          totalRevenue: 0,
          totalOrders: 0,
          totalCustomers: 0,
        };
      }
      
      brandMetrics[brand].stores.push(store);
      brandMetrics[brand].totalRevenue += store.totalSales || 0;
      brandMetrics[brand].totalOrders += store.orderCount || 0;
      brandMetrics[brand].totalCustomers += store.orderCount || 0; // Using orders as proxy for customers
    });

    // Convert to final format
    return Object.values(brandMetrics).map((metrics: any) => {
      const avgOrderValue = metrics.totalOrders > 0 
        ? metrics.totalRevenue / metrics.totalOrders 
        : 0;
      
      // Find top performing store
      const topStore = metrics.stores.reduce((best: any, store: any) => {
        if (!best || (store.totalSales || 0) > (best.totalSales || 0)) {
          return store;
        }
        return best;
      }, null);
      
      return {
        brand: metrics.brand,
        stores: metrics.stores.length,
        revenue: metrics.totalRevenue,
        orders: metrics.totalOrders,
        avgOrderValue,
        customers: metrics.totalCustomers,
        growth: 0, // Would need historical data for growth
        topStore: topStore?.name || topStore?.storeName || 'N/A',
        performance: metrics.totalRevenue > 0 ? 100 : 0, // Simple performance indicator
      };
    });
  }, [storesData, storesWithSales, selectedStores]);

  const totalRevenue = brandData.reduce((sum, b) => sum + b.revenue, 0);
  const totalOrders = brandData.reduce((sum, b) => sum + b.orders, 0);
  const totalStores = brandData.reduce((sum, b) => sum + b.stores, 0);

  if (isStoresLoading || isSalesLoading) {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Brand Performance Overview
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Card sx={{ ...glassStyle, p: 3, height: 300 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="text" width="80%" height={24} sx={{ mt: 2 }} />
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="rectangular" width="100%" height={100} sx={{ mt: 2 }} />
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
        Brand Performance Overview
      </Typography>

      <Grid container spacing={3}>
        {brandData.map((brand, index) => (
          <Grid item xs={12} sm={6} lg={3} key={brand.brand}>
            <motion.div {...animationVariants.scale} transition={{ delay: index * 0.1 }}>
              <Card
                sx={{
                  ...glassStyle,
                  p: 3,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  borderTop: `4px solid ${brandColors[brand.brand]}`,
                  transition: 'all 0.3s ease',
                  cursor: onBrandClick ? 'pointer' : 'default',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 32px ${alpha(brandColors[brand.brand], 0.3)}`,
                  },
                }}
                onClick={() => onBrandClick?.(brand.brand)}
              >
                {/* Brand Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      backgroundColor: alpha(brandColors[brand.brand], 0.1),
                      color: brandColors[brand.brand],
                      mr: 2,
                    }}
                  >
                    {brandIcons[brand.brand]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {brand.brand}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {brand.stores} stores
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    icon={brand.growth >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    label={`${Math.abs(brand.growth).toFixed(1)}%`}
                    sx={{
                      backgroundColor: alpha(
                        brand.growth >= 0 ? theme.palette.success.main : theme.palette.error.main,
                        0.1
                      ),
                      color: brand.growth >= 0 ? theme.palette.success.main : theme.palette.error.main,
                    }}
                  />
                </Box>

                {/* Revenue */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Revenue
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${brand.revenue.toLocaleString()}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(brand.revenue / totalRevenue) * 100}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: alpha(brandColors[brand.brand], 0.1),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 2,
                        backgroundColor: brandColors[brand.brand],
                      },
                    }}
                  />
                </Box>

                {/* Key Metrics */}
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Orders
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {brand.orders.toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Avg Order
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${brand.avgOrderValue.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Customers
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {brand.customers.toLocaleString()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Performance
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {brand.performance}%
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Top Store */}
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Top Performing Store
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {brand.topStore}
                  </Typography>
                </Box>

                {/* Background Icon */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    opacity: 0.05,
                    transform: 'rotate(-15deg)',
                    '& svg': {
                      fontSize: 150,
                    },
                  }}
                >
                  {brandIcons[brand.brand]}
                </Box>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Summary Card */}
      <motion.div {...animationVariants.fadeIn} transition={{ delay: 0.5 }}>
        <Card sx={{ ...glassStyle, p: 3, mt: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <StoreIcon color="primary" />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totalStores}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Stores
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: alpha(theme.palette.success.main, 0.1) }}>
                  <MoneyIcon color="success" />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    ${(totalRevenue / 1000).toFixed(1)}K
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                  <ReceiptIcon color="info" />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {(totalOrders / 1000).toFixed(1)}K
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: alpha(theme.palette.warning.main, 0.1) }}>
                  <PeopleIcon color="warning" />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    ${(totalRevenue / totalOrders).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Order Value
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </motion.div>
    </Box>
  );
};

export default BrandOverview;